/**
 * Governance Attack Paths Page (F070)
 *
 * Shows how governance failures propagate across the delivery infrastructure.
 * Wiz-style attack path visualization for governance.
 *
 * Route: /attack-paths | Nav: Intelligence > Attack Paths
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Dropdown,
  Option,
  Divider,
} from '@fluentui/react-components';
import {
  ShieldErrorRegular,
  ArrowRightRegular,
  DocumentSearchRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyStateCard } from '../components/common/EmptyStateCard';
import { governanceTokens } from '../components/design-system';

interface AttackPathNode {
  id: string;
  type: 'event' | 'policy' | 'control' | 'evidence' | 'framework' | 'risk';
  label: string;
  severity?: 'critical' | 'high' | 'medium' | 'low';
}

interface AttackPath {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'mitigated' | 'resolved';
  nodes: AttackPathNode[];
  impactedFrameworks: number;
  impactedControls: number;
  createdAt: string;
}

const nodeTypeColors: Record<AttackPathNode['type'], string> = {
  event: governanceTokens.colorStatusError,
  policy: governanceTokens.colorStatusWarning,
  control: governanceTokens.colorGovernanceSecondary,
  evidence: governanceTokens.colorGovernancePrimary,
  framework: '#8852E0',
  risk: governanceTokens.colorStatusError,
};

const severityColors: Record<string, 'danger' | 'warning' | 'success' | 'informative'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

// Demo data — will be replaced with API calls when backend is ready
const demoPaths: AttackPath[] = [
  {
    id: '1',
    title: 'Secret committed in payment-api PR #41',
    severity: 'critical',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'event', label: 'Secret detected in config.js', severity: 'critical' },
      { id: 'n2', type: 'policy', label: 'No secrets in source code' },
      { id: 'n3', type: 'control', label: 'AC-2: Secret Management' },
      { id: 'n4', type: 'evidence', label: 'Evidence chain incomplete' },
      { id: 'n5', type: 'framework', label: 'SOC2 CC6.1' },
      { id: 'n6', type: 'risk', label: 'Audit risk elevated', severity: 'high' },
    ],
    impactedFrameworks: 2,
    impactedControls: 3,
    createdAt: '2026-03-07T10:00:00Z',
  },
  {
    id: '2',
    title: 'Dependency vulnerability in auth-service',
    severity: 'high',
    status: 'active',
    nodes: [
      { id: 'n1', type: 'event', label: 'CVE-2026-1234 in lodash@4.17.15', severity: 'high' },
      { id: 'n2', type: 'policy', label: 'Dependency scanning required' },
      { id: 'n3', type: 'control', label: 'SC-7: Dependency Management' },
      { id: 'n4', type: 'framework', label: 'NIST 800-53 SC-7' },
      { id: 'n5', type: 'risk', label: 'Supply chain risk', severity: 'high' },
    ],
    impactedFrameworks: 1,
    impactedControls: 2,
    createdAt: '2026-03-06T14:30:00Z',
  },
];

export const AttackPathsPage: React.FC = () => {
  const ps = usePageStyles();
  const [selectedPath, setSelectedPath] = useState<AttackPath | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  const filteredPaths = severityFilter === 'all'
    ? demoPaths
    : demoPaths.filter(p => p.severity === severityFilter);

  return (
    <div className={ps.page}>
      <PageHeader
        title="Governance Attack Paths"
        subtitle="Trace how governance failures propagate across your delivery infrastructure"
        icon={<ShieldErrorRegular />}
        iconColor={governanceTokens.colorStatusError}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center' }}>
        <Body2>Severity:</Body2>
        <Dropdown value={severityFilter} onOptionSelect={(_, d) => setSeverityFilter(d.optionValue as string)} style={{ minWidth: '120px' }}>
          <Option value="all">All</Option>
          <Option value="critical">Critical</Option>
          <Option value="high">High</Option>
          <Option value="medium">Medium</Option>
          <Option value="low">Low</Option>
        </Dropdown>
        <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{filteredPaths.length} paths found</Body2>
      </div>

      <div className={ps.twoColumn}>
        {/* Path List */}
        <Card>
          <CardHeader header={<Title2>Attack Paths</Title2>} />
          <div className={ps.cardBody}>
            {filteredPaths.length > 0 ? (
              <div className={ps.rowList}>
                {filteredPaths.map(path => (
                  <div
                    key={path.id}
                    className={ps.rowItem}
                    style={{
                      cursor: 'pointer',
                      borderLeft: `3px solid ${nodeTypeColors[path.severity === 'critical' ? 'event' : 'policy']}`,
                      backgroundColor: selectedPath?.id === path.id ? tokens.colorNeutralBackground3 : undefined,
                    }}
                    onClick={() => setSelectedPath(path)}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flex: 1 }}>
                      <Body1 style={{ fontWeight: 500 }}>{path.title}</Body1>
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                        <Badge appearance="filled" color={severityColors[path.severity]} size="small">{path.severity}</Badge>
                        <Badge appearance="tint" color="informative" size="small">{path.impactedFrameworks} frameworks</Badge>
                        <Badge appearance="tint" color="informative" size="small">{path.impactedControls} controls</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyStateCard
                title="No attack paths found"
                description="No active attack paths detected. System Status: All governance enforcement points are operating. No failure propagation chains identified."
                icon={<DocumentSearchRegular />}
              />
            )}
          </div>
        </Card>

        {/* Path Detail / Explorer */}
        <Card>
          <CardHeader header={<Title2>{selectedPath ? 'Attack Path Detail' : 'Select a Path'}</Title2>} />
          <div className={ps.cardBody}>
            {selectedPath ? (
              <>
                <Body1 style={{ fontWeight: 600 }}>{selectedPath.title}</Body1>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                  <Badge appearance="filled" color={severityColors[selectedPath.severity]}>{selectedPath.severity}</Badge>
                  <Badge appearance="tint" color={selectedPath.status === 'active' ? 'danger' : 'success'}>{selectedPath.status}</Badge>
                </div>
                <Divider />
                <Body2 style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>Propagation Chain</Body2>

                {/* Attack path flow visualization */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                  {selectedPath.nodes.map((node, idx) => (
                    <div key={node.id} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                      {idx > 0 && (
                        <div style={{ width: '2px', height: '16px', backgroundColor: nodeTypeColors[node.type], marginLeft: '15px', marginTop: '-8px', marginBottom: '-8px', position: 'absolute' }} />
                      )}
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: nodeTypeColors[node.type],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Body2 style={{ color: '#fff', fontSize: '11px', fontWeight: 600 }}>
                          {node.type.slice(0, 2).toUpperCase()}
                        </Body2>
                      </div>
                      <div style={{ flex: 1 }}>
                        <Body2>{node.label}</Body2>
                        <Body2 style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase100 }}>
                          {node.type}
                        </Body2>
                      </div>
                      {idx < selectedPath.nodes.length - 1 && (
                        <ArrowRightRegular style={{ color: tokens.colorNeutralForeground3 }} />
                      )}
                    </div>
                  ))}
                </div>

                <Divider />
                <Body2 style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>Impact Summary</Body2>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalL }}>
                  <div className={ps.statTile} style={{ flex: 1 }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Frameworks</Body2>
                    <Title2>{selectedPath.impactedFrameworks}</Title2>
                  </div>
                  <div className={ps.statTile} style={{ flex: 1 }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Controls</Body2>
                    <Title2>{selectedPath.impactedControls}</Title2>
                  </div>
                </div>
              </>
            ) : (
              <Body1 className={ps.emptyState}>
                Select an attack path to view the propagation chain and impact analysis.
              </Body1>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
